import { Injectable, inject } from '@angular/core';
import {
  ClearNotaryOnboarding,
  GetCompany,
  GetFullProfile,
  SetAADUserWithClaims,
  SetCompany,
  SetFullProfile,
  SetNotaryOnboarding,
  SetNotaryStamp,
  SetNotaryStampAsPrimary,
  SetSelfProfile,
  SetTwoFactorEnabled,
  UpdateFullProfile,
  UpdateSelfProfile,
} from './profile.actions';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { produce } from 'immer';
import {
  CompanyData,
  NotaryOnboarding,
  Profile,
  ProfileUserTypeEnum,
  SelfProfile,
  StampWithImage,
} from '@et/typings';
import { ProfilesService } from '@escrowtab/v2profile-api-client';
import { CompanyService } from '@et/shared';
import { AccountInfo } from '@azure/msal-browser';

export interface ProfileStateModel {
  notaryStamps: StampWithImage[] | undefined;
  aadProfileWithClaims: AccountInfo | undefined;
  selfProfile: SelfProfile | undefined;
  company: CompanyData | undefined;
  fullProfile: Profile | undefined;
  notaryOnboarding: NotaryOnboarding | undefined;
  error: string | undefined;
  /** Better Auth's live `user.twoFactorEnabled`; `undefined` until the post-login
   * `get-session` call resolves — see `SetTwoFactorEnabled`. */
  twoFactorEnabled: boolean | undefined;
}

@State<ProfileStateModel>({
  name: 'ProfileStore',
  defaults: {
    notaryStamps: undefined,
    aadProfileWithClaims: undefined,
    selfProfile: undefined,
    company: undefined,
    notaryOnboarding: undefined,
    fullProfile: undefined,
    error: undefined,
    twoFactorEnabled: undefined,
  },
})
@Injectable()
export class ProfileState {
  private profileService = inject(ProfilesService);
  private companyService = inject(CompanyService);

  @Selector()
  static v2ProfileId(state: ProfileStateModel) {
    if (state.selfProfile?.profileId) {
      return state.selfProfile.profileId;
    }
    if (state.fullProfile?.profileId) {
      return state.fullProfile.profileId;
    }
    return undefined;
  }

  @Selector()
  static getSelfProfile(state: ProfileStateModel) {
    return state.selfProfile;
  }

  @Selector()
  static getCompanyId(state: ProfileStateModel): string | undefined {
    if (state.selfProfile?.company?.companyId) {
      return state.selfProfile.company.companyId;
    }
    return state.company?.companyId;
  }

  @Selector()
  static getProfileDisplayName(state: ProfileStateModel): string | undefined {
    if (state.selfProfile) {
      return ProfileState.buildDisplayName(state.selfProfile);
    }
    if (state.fullProfile) {
      return ProfileState.buildDisplayName(state.fullProfile);
    }
    return undefined;
  }

  @Selector()
  static getProfileEmail(state: ProfileStateModel) {
    if (state.selfProfile?.email) {
      return state.selfProfile.email;
    }
    return state.fullProfile?.email;
  }

  @Selector()
  static getCompany(state: ProfileStateModel) {
    return state.company;
  }

  @Selector()
  static getNotaryStamp(state: ProfileStateModel) {
    return state.notaryStamps;
  }

  @Selector()
  static getNotaryPrimaryStamp(
    state: ProfileStateModel,
  ): StampWithImage | undefined {
    const approvedStamps = state.notaryStamps?.filter(
      (stamp) => stamp.isApproved,
    );

    if (!approvedStamps || approvedStamps.length === 0) {
      return undefined;
    }

    const primaryStamps = approvedStamps.filter((stamp) => stamp.isPrimary);
    return primaryStamps.length >= 1 ? primaryStamps[0] : undefined;
  }

  @Selector()
  static getAadProfileWithClaims(state: ProfileStateModel) {
    return state.aadProfileWithClaims as AccountInfo;
  }

  @Selector()
  static getNotaryOnboarding(state: ProfileStateModel) {
    return state.notaryOnboarding;
  }

  @Selector()
  static getUserType(state: ProfileStateModel) {
    if (state.selfProfile) {
      return ProfileUserTypeEnum[state.selfProfile.userType];
    }
    if (typeof state.fullProfile?.userType === 'number') {
      return ProfileUserTypeEnum[state.fullProfile.userType];
    }
    return state.fullProfile?.userType;
  }

  @Selector()
  static getIsCompanyAdmin(state: ProfileStateModel): boolean {
    if (state.selfProfile) {
      return state.selfProfile.isCompanyAdmin;
    }
    return !!state.fullProfile?.isCompanyAdmin;
  }

  @Selector()
  static getIsEtAdmin(state: ProfileStateModel): boolean {
    if (state.selfProfile) {
      return state.selfProfile.isEtAdmin;
    }
    return !!state.fullProfile?.isEtAdmin;
  }

  @Selector()
  static getAllProfiles(state: ProfileStateModel) {
    return state;
  }

  @Selector()
  static getRequiredMfaMethod(state: ProfileStateModel) {
    return state.selfProfile?.requiredMfaMethod;
  }

  @Selector()
  static getTwoFactorEnabled(state: ProfileStateModel): boolean | undefined {
    return state.twoFactorEnabled;
  }

  private static buildDisplayName(profile: {
    firstName?: string | null;
    lastName?: string | null;
  }): string | undefined {
    const first = profile.firstName || '';
    const last = profile.lastName || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || undefined;
  }

  @Selector()
  static getFullProfile(state: ProfileStateModel) {
    return state.fullProfile;
  }

  @Selector()
  static isElsiEnabled(state: ProfileStateModel) {
    return state.company?.elsiEnabled;
  }

  @Action(SetAADUserWithClaims)
  setAADUserWithClaims(
    ctx: StateContext<ProfileStateModel>,
    { profile }: SetAADUserWithClaims,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.aadProfileWithClaims = profile as AccountInfo;
    });
    ctx.setState(state);
  }

  @Action(SetSelfProfile)
  setSelfProfile(
    ctx: StateContext<ProfileStateModel>,
    { selfProfile }: SetSelfProfile,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.selfProfile = selfProfile;
    });
    ctx.setState(state);
  }

  @Action(UpdateSelfProfile)
  updateSelfProfile(
    ctx: StateContext<ProfileStateModel>,
    { selfProfile }: UpdateSelfProfile,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.selfProfile = {
        ...draft.selfProfile,
        ...selfProfile,
      } as SelfProfile;
    });
    ctx.setState(state);
  }

  @Action(GetFullProfile)
  getProfileFromApi(
    ctx: StateContext<ProfileStateModel>,
    { profileId }: GetFullProfile,
  ) {
    this.profileService
      .getProfile({
        id: profileId,
      })
      .subscribe(({ data }) => {
        ctx.dispatch(new SetFullProfile(data as Profile));
      });
  }

  @Action(SetFullProfile)
  setProfileFromApi(
    ctx: StateContext<ProfileStateModel>,
    payload: SetFullProfile,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.fullProfile = payload.profile;
    });
    ctx.setState(state);
  }

  @Action(UpdateFullProfile)
  updateFullProfile(
    ctx: StateContext<ProfileStateModel>,
    { profile }: UpdateFullProfile,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.fullProfile = { ...draft.fullProfile, ...profile };
    });
    ctx.setState(state);
  }

  @Action(GetCompany)
  getCompanyFromApi(
    ctx: StateContext<ProfileStateModel>,
    { companyId }: GetCompany,
  ) {
    return this.companyService
      .getCompanyById(companyId)
      .subscribe((company) => {
        ctx.dispatch(new SetCompany(company));
      });
  }

  @Action(SetCompany)
  setCompany(ctx: StateContext<ProfileStateModel>, { company }: SetCompany) {
    const state = produce(ctx.getState(), (draft) => {
      draft.company = company;
    });
    ctx.setState(state);
  }

  @Action(SetNotaryStamp)
  setNotaryStamp(
    ctx: StateContext<ProfileStateModel>,
    { stamp }: SetNotaryStamp,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.notaryStamps = stamp;
    });
    ctx.setState(state);
  }

  @Action(SetNotaryStampAsPrimary)
  makeNotaryStampPrimary(
    ctx: StateContext<ProfileStateModel>,
    { stampId }: SetNotaryStampAsPrimary,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.notaryStamps = draft.notaryStamps?.map((s) => {
        s.isPrimary = s.stampId === stampId;
        return s;
      });
    });
    ctx.setState(state);
  }

  @Action(SetNotaryOnboarding)
  setNotaryOnboarding(
    ctx: StateContext<ProfileStateModel>,
    { payload }: SetNotaryOnboarding,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.notaryOnboarding = { ...draft.notaryOnboarding, ...payload };
    });
    ctx.setState(state);
  }

  @Action(ClearNotaryOnboarding)
  clearNotaryOnboarding(ctx: StateContext<ProfileStateModel>) {
    const state = produce(ctx.getState(), (draft) => {
      draft.notaryOnboarding = undefined;
    });
    ctx.setState(state);
  }

  @Action(SetTwoFactorEnabled)
  setTwoFactorEnabled(
    ctx: StateContext<ProfileStateModel>,
    { twoFactorEnabled }: SetTwoFactorEnabled,
  ) {
    const state = produce(ctx.getState(), (draft) => {
      draft.twoFactorEnabled = twoFactorEnabled;
    });
    ctx.setState(state);
  }
}
